/**
 * Parses `/customers/:customerId` and `/customers/:customerId/projects/:projectId`
 * out of a pathname. Used by components rendered as siblings of <Routes>
 * (the support chat bubble, the Quick Start modal) where useParams() can't
 * see those route params.
 */
export function parseCustomerProjectRoute(pathname: string): { customerId?: string; projectId?: string } {
  const match = pathname.match(/^\/customers\/([^/]+)(?:\/projects\/([^/]+))?/);
  if (!match) return {};
  return { customerId: match[1], projectId: match[2] };
}

export type ScreenRouteKind =
  | 'home'
  | 'customer'
  | 'project'
  | 'report'
  | 'comparison'
  | 'proposal'
  | 'admin'
  | 'sysadmin'
  | 'settings'
  | 'support'
  | 'other';

/**
 * Broader route classification used by useScreenContext() — the assistant's
 * deterministic screen-context sentence builder needs to know not just the
 * customer/project ids but what *kind* of page this is (report vs comparison
 * vs a plain admin/settings page) so the backend sentence is accurate.
 */
export function parseScreenRoute(pathname: string): { kind: ScreenRouteKind; customerId?: string; projectId?: string; proposalId?: string } {
  const clean = pathname.split('?')[0].split('#')[0];

  if (clean === '/' ) return { kind: 'home' };
  if (clean.startsWith('/workflow-report')) return { kind: 'report' };
  if (clean.startsWith('/comparison')) return { kind: 'comparison' };
  if (clean.startsWith('/proposal-editor/')) {
    const match = clean.match(/^\/proposal-editor\/([^/]+)/);
    return { kind: 'proposal', proposalId: match?.[1] };
  }
  if (clean.startsWith('/pdf-export-preview')) return { kind: 'report' };
  if (clean.startsWith('/admin')) return { kind: 'admin' };
  if (clean.startsWith('/sysadmin')) return { kind: 'sysadmin' };
  if (clean.startsWith('/settings') || clean.startsWith('/change-password')) return { kind: 'settings' };
  if (clean.startsWith('/support')) return { kind: 'support' };
  if (clean.startsWith('/customers/')) {
    const { customerId, projectId } = parseCustomerProjectRoute(clean);
    return { kind: projectId ? 'project' : 'customer', customerId, projectId };
  }
  return { kind: 'other' };
}
